
import React, { useEffect, useRef, useState } from "react";
import { authFetch } from "../../../api/api"; 


const BASE_URL = process.env.REACT_APP_DJANGO_BACKEND;
const API_URL = `${BASE_URL}/api/accounting/accounting-records/`;



function documentUrl(doc) {
  if (doc.file_url) return doc.file_url;

  if (doc.file?.startsWith("http")) return doc.file;

  return `${BASE_URL}${doc.file?.startsWith("/") ? "" : "/"}${doc.file || ""}`;
}

function isImage(doc) {
  const name = (doc.original_name || doc.file || "").toLowerCase();
  return /\.(jpg|jpeg|png|webp|gif|bmp)$/.test(name);
}

function isPdf(doc) {
  const name = (doc.original_name || doc.file || "").toLowerCase();
  return name.endsWith(".pdf");
}

export default function PaymentDocumentsModal({
  payment,
  onClose,
  onDocumentsChanged,
}) {
  const [documents, setDocuments] = useState(payment.documents || []);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const documentsUrl = `${API_URL}${payment.id}/documents/`;

  async function loadDocuments() {
    setLoading(true);
    setError("");

    try {
      const response = await authFetch(documentsUrl);
      if (!response.ok) {
        throw new Error(`Не удалось загрузить документы (${response.status})`);
      }

      const data = await response.json();
      setDocuments(data);
      onDocumentsChanged?.(payment.id, data);
    } catch (err) {
      setError(err.message || "Ошибка загрузки документов");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadDocuments();
    // Загружаем документы при открытии окна для выбранного платежа.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment.id]);

  async function uploadFiles(event) {
    const files = Array.from(event.target.files || []);
    event.target.value = "";

    if (!files.length) return;

    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));

    setUploading(true);
    setError("");

    try {
      const response = await authFetch(documentsUrl, {
        method: "POST",
        body: formData,
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data.detail || `Не удалось загрузить файлы (${response.status})`
        );
      }

      await loadDocuments();
    } catch (err) {
      setError(err.message || "Ошибка загрузки файлов");
    } finally {
      setUploading(false);
    }
  }

  async function deleteDocument(doc) {
    setError("");

    try {
       const response = await authFetch(
        `${BASE_URL}/api/accounting/accounting-documents/${doc.id}/`,
        {
          method: "DELETE",
        }
      );

      if (!response.ok) {
        throw new Error(`Не удалось удалить документ (${response.status})`);
      }

      setDeleteTarget(null);

      if (preview?.id === doc.id) {
        setPreview(null);
      }

      await loadDocuments();
    } catch (err) {
      setError(err.message || "Ошибка удаления документа");
    }
  }


  const downloadZip = async () => {
    try {
      const response = await authFetch(
        `${API_URL}${payment.id}/documents/zip/`,
        {
          method: "GET",
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("ZIP download error:", {
          status: response.status,
          body: errorText,
        });

        throw new Error(
          `Ошибка сервера ${response.status}: ${
            errorText || "не удалось скачать архив"
          }`
        );
      }

      const blob = await response.blob();

      if (blob.size === 0) {
        throw new Error("Сервер вернул пустой архив");
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `payment-${payment.id}-documents.zip`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      // Не отзывать URL сразу после клика.
      window.setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
    } catch (error) {
      console.error("Не удалось скачать ZIP:", error);
      alert(error.message || "Не удалось скачать архив");
    }
  };


  
  async function downloadOne(doc) {
    setError("");

    try {
      const response = await authFetch(documentUrl(doc));

      if (!response.ok) {
        throw new Error(`Не удалось скачать файл (${response.status})`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = doc.original_name || `document-${doc.id}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || "Ошибка скачивания файла");
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="flex items-start justify-between border-b p-5">
          <div>
            <h2 className="text-xl font-bold">
              Подтверждающие документы
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Платёж № {payment.id} · {payment.money_sum || "—"} CNY
            </p>
          </div>

          <button
            type="button"
            className="rounded px-3 py-1 text-xl text-gray-500 hover:bg-gray-100"
            onClick={onClose}
            aria-label="Закрыть"
          >
            ×
          </button>
        </div>

        <div className="flex flex-wrap gap-2 border-b p-4">
          <button
            type="button"
            className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          >
            {uploading ? "Загрузка…" : "Добавить подтверждающий документ"}
          </button>

          <input
            ref={inputRef}
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={uploadFiles}
          />

          <button
            type="button"
            className="rounded border px-4 py-2 hover:bg-gray-50 disabled:opacity-50"
            disabled={loading || documents.length === 0}
            onClick={downloadZip}
          >
            Скачать все ZIP
          </button>
        </div>

        {error && (
          <div className="mx-4 mt-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="min-h-40 overflow-y-auto p-4">
          {loading ? (
            <p className="py-8 text-center text-gray-500">
              Загрузка документов…
            </p>
          ) : documents.length === 0 ? (
            <p className="py-8 text-center text-gray-500">
              Документов пока нет. Добавьте подтверждение платежа.
            </p>
          ) : (
            <ul className="space-y-3">
              {documents.map((doc) => (
                <li
                  key={doc.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3"
                >
                  <button
                    type="button"
                    className="min-w-0 flex-1 text-left"
                    onClick={() => setPreview(doc)}
                  >
                    <span className="block truncate font-medium text-blue-700 hover:underline">
                      {doc.original_name || `Документ ${doc.id}`}
                    </span>
                    <span className="text-xs text-gray-500">
                      {isPdf(doc)
                        ? "PDF"
                        : isImage(doc)
                          ? "Изображение"
                          : "Файл"}
                      {doc.uploaded_at
                        ? ` · ${new Date(doc.uploaded_at).toLocaleString()}`
                        : ""}
                    </span>
                  </button>

                  <div className="flex flex-wrap gap-3 text-sm">
                    <button
                      type="button"
                      className="text-blue-700 hover:underline"
                      onClick={() => downloadOne(doc)}
                    >
                      Скачать
                    </button>

                    <button
                      type="button"
                      className="text-red-600 hover:underline"
                      onClick={() => setDeleteTarget(doc)}
                    >
                      Удалить
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex justify-end border-t p-4">
          <button
            type="button"
            className="rounded border px-4 py-2 hover:bg-gray-50"
            onClick={onClose}
          >
            Закрыть
          </button>
        </div>
      </div>

      {preview && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setPreview(null);
          }}
        >
          <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white">
            <div className="flex items-center justify-between gap-3 border-b p-3">
              <span className="truncate font-medium">
                {preview.original_name || `Документ ${preview.id}`}
              </span>
              <div className="flex gap-3">
                <button
                  type="button"
                  className="text-blue-700"
                  onClick={() => downloadOne(preview)}
                >
                  Скачать
                </button>
                <button
                  type="button"
                  className="text-gray-600"
                  onClick={() => setPreview(null)}
                >
                  Закрыть
                </button>
              </div>
            </div>

            <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto bg-gray-100 p-3">
              {isImage(preview) ? (
                <img
                  src={documentUrl(preview)}
                  alt={preview.original_name || "Документ"}
                  className="max-h-[75vh] max-w-full object-contain"
                />
              ) : isPdf(preview) ? (
                <iframe
                  title={preview.original_name || "PDF"}
                  src={documentUrl(preview)}
                  className="h-[75vh] w-full bg-white"
                />
              ) : (
                <div className="p-8 text-center">
                  <p className="mb-3">
                    Предпросмотр этого типа файла недоступен.
                  </p>
                  <button
                    type="button"
                    className="text-blue-700 underline"
                    onClick={() => downloadOne(preview)}
                  >
                    Скачать файл
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-bold">
              Удалить подтверждающий документ?
            </h3>
            <p className="my-4 text-sm text-gray-600">
              Вы уверены, что хотите удалить «
              {deleteTarget.original_name || `Документ ${deleteTarget.id}`}»?
              Это действие нельзя отменить.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                className="rounded border px-4 py-2"
                onClick={() => setDeleteTarget(null)}
              >
                Отмена
              </button>
              <button
                type="button"
                className="rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700"
                onClick={() => deleteDocument(deleteTarget)}
              >
                Да, удалить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
